<ph-stack [attr.aria-busy]="intakeBusy || isSaving ? 'true' : null">
    <ph-page-header dense size="small" [level]="3">{{ isEdit ? 'Edit workflow' : 'New workflow' }}</ph-page-header>

    <ph-stepper [items]="stepLabels"
                [current]="stepIndex"
                [reached]="furthest"
                [disabled]="intakeBusy || isSaving"
                (ph-step)="goToStep(steps[$event.detail].key)"></ph-stepper>

    <ph-card flat class="step-body" [heading]="stepTitle" [ngSwitch]="step">
        <ph-stack *ngSwitchCase="'when'" data-test="step-when">

            <ph-radio-group variant="card"
                            orientation="horizontal"
                            aria-label="What starts the workflow"
                            [items]="triggerTypeItems"
                            [value]="isEventTrigger ? 'event' : 'schedule'"
                            (ph-change)="$event.detail === 'event' ? useEventTrigger() : useScheduleTrigger()"></ph-radio-group>

            <ng-container *ngIf="isEventTrigger">
                <ph-stack [formGroup]="eventForm" data-test="event-pickers">
                    <ph-stack gap="tight" data-test="event-box">
                        <ph-stack gap="tight">
                            <ph-label id="gable-workflow-event-label" required>What happens</ph-label>
                            <ph-input type="text"
                                      icon="magnifying-glass"
                                      clearable
                                      aria-label="Search triggers"
                                      placeholder="Search triggers"
                                      [formControl]="triggerSearch"
                                      ngDefaultControl
                                      data-test="trigger-search"></ph-input>
                            <ph-hint *ngIf="triggersLoading" role="status">Loading triggers</ph-hint>
                            <ph-hint has-error *ngIf="triggersLoadFailed" role="alert">
                                Couldn't load the triggers for this organization.
                                <ph-link-button (click)="loadTriggers()">Try again</ph-link-button>
                            </ph-hint>
                            <ph-stack *ngIf="triggerGroups.length > 0">
                                <ph-stack *ngFor="let g of triggerGroups; trackBy: trackByGroup" gap="tight">
                                    <ph-label [attr.data-test]="'trigger-group-' + g.name">{{ g.name }}</ph-label>
                                    <ph-stack gap="snug" role="radiogroup" [attr.aria-label]="g.name">
                                        <ph-list-row *ngFor="let t of g.triggers; trackBy: trackByKey"
                                                     variant="card"
                                                     role="radio"
                                                     [attr.aria-checked]="isSelectedTrigger(t)"
                                                     [attr.data-test]="'trigger-' + t.key"
                                                     [heading]="triggerRowLabel(t)"
                                                     [meta]="t.description"
                                                     wrap
                                                     [open]="isSelectedTrigger(t)"
                                                     (ph-select)="selectTrigger(t.key)">
                                            <ph-stack slot="detail" *ngIf="isSelectedTrigger(t)" data-test="trigger-controls">
                                                <ph-field *ngIf="daysParam as p" data-test="trigger-days">
                                                    <ph-label required>{{ p.label }}</ph-label>
                                                    <ph-input type="number"
                                                              inputmode="numeric"
                                                              step="1"
                                                              formControlName="days"
                                                              ngDefaultControl
                                                              [suffix]="p.unit"
                                                              [min]="p.min"
                                                              [max]="p.max"></ph-input>
                                                    <ph-hint has-error *ngIf="daysError" role="alert" data-test="trigger-days-error">{{ daysError }}</ph-hint>
                                                </ph-field>

                                                <ph-field *ngIf="t.filter as f" data-test="event-filter">
                                                    <ph-label [required]="f.required">{{ f.label }}</ph-label>
                                                    <ph-hint>{{ f.required ? 'Pick at least one.' : 'Leave all unselected for any.' }}</ph-hint>
                                                    <ph-toolbar gap="tight">
                                                        <ph-chip *ngFor="let o of visibleFilterOptions(f); trackBy: trackByValue"
                                                                 size="small"
                                                                 [selected]="isFilterChecked(o.value)"
                                                                 (click)="toggleFilter(o.value, !isFilterChecked(o.value))">{{ o.label }}</ph-chip>
                                                        <ph-link-button *ngIf="hiddenFilterCount(f) as hidden"
                                                                        variant="primary"
                                                                        size="small"
                                                                        data-test="filter-show-more"
                                                                        (click)="filtersExpanded = true">Show {{ hidden }} more</ph-link-button>
                                                        <ph-link-button *ngIf="filtersExpanded && canCollapseFilters(f)"
                                                                        variant="primary"
                                                                        size="small"
                                                                        data-test="filter-show-fewer"
                                                                        (click)="filtersExpanded = false">Show fewer</ph-link-button>
                                                    </ph-toolbar>
                                                    <ph-hint has-error *ngIf="filterError" role="alert" data-test="event-filter-error">{{ filterError }}</ph-hint>
                                                </ph-field>
                                            </ph-stack>
                                        </ph-list-row>
                                    </ph-stack>
                                </ph-stack>
                            </ph-stack>
                            <ph-hint *ngIf="triggerSearchNoMatch" role="status" data-test="trigger-search-empty">{{ triggerSearchEmpty }}</ph-hint>
                        </ph-stack>
                    </ph-stack>

                    <ph-card flat *ngIf="eventPreview">
                        <strong data-test="event-preview">{{ eventPreview }}</strong>
                    </ph-card>
                </ph-stack>

                <div data-test="trigger-request">
                    <ph-link-button data-test="request-trigger-link" (click)="openTriggerRequest()">Don't see the trigger you need? Tell us</ph-link-button>
                </div>
                <ph-dialog [open]="requestOpen" size="sm" heading="Request a trigger" description="Describe what should start the workflow. The PayHOA team reads every request." (ph-close)="requestOpen = false">
                    <form [formGroup]="requestForm" (ngSubmit)="sendTriggerRequest()" novalidate>
                        <ph-field>
                            <ph-label required>What should start a workflow?</ph-label>
                            <ph-textarea formControlName="text"
                                         ngDefaultControl
                                         rows="3"
                                         [maxlength]="triggerRequestMax"
                                         placeholder="When a homeowner pays off an overdue balance"></ph-textarea>
                            <ph-hint>{{ requestForm.controls.text.value.length }} / {{ triggerRequestMax }}</ph-hint>
                        </ph-field>
                    </form>
                    <ph-actions slot="footer">
                        <ph-button variant="secondary" mode="filled" size="small" (click)="requestOpen = false">Cancel</ph-button>
                        <ph-button size="small" [disabled]="requestSending" data-test="send-trigger-request" (click)="sendTriggerRequest()">{{ requestSending ? 'Sending' : 'Send' }}</ph-button>
                    </ph-actions>
                </ph-dialog>
            </ng-container>

            <ph-stack *ngIf="!isEventTrigger" [formGroup]="pickersForm" data-test="cadence-pickers">
                <ph-columns min="200px">
                    <ph-field>
                        <ph-label required>Frequency</ph-label>
                        <ph-select formControlName="frequency" ngDefaultControl [items]="frequencies"></ph-select>
                    </ph-field>

                    <ph-field>
                        <ph-label required>Time</ph-label>
                        <ph-input type="time" step="900" formControlName="time" ngDefaultControl></ph-input>
                    </ph-field>
                </ph-columns>

                <ph-stack gap="tight" *ngIf="frequency === 'weekly'" role="group" aria-labelledby="gable-workflow-weekdays-label" data-test="weekly-box">
                    <ph-label required id="gable-workflow-weekdays-label">Days of the week</ph-label>
                    <ph-toolbar gap="tight">
                        <ph-chip *ngFor="let d of weekDays; trackBy: trackByValue"
                                 size="small"
                                 [selected]="hasWeekday(d.value)"
                                 (click)="toggleWeekday(d.value, !hasWeekday(d.value))"
                                 [attr.data-test]="'weekday-' + d.value">{{ d.label.slice(0, 3) }}</ph-chip>
                    </ph-toolbar>
                </ph-stack>

                <ph-stack gap="tight" *ngIf="frequency === 'monthly'" data-test="monthly-box">
                    <ph-field>
                        <ph-label required id="gable-workflow-monthly-mode-label">Repeats on</ph-label>
                        <ph-radio-group formControlName="monthlyMode"
                                        ngDefaultControl
                                        orientation="horizontal"
                                        aria-label="Repeats on"
                                        [items]="[{value: 'day', label: 'A day of the month'}, {value: 'weekday', label: 'A weekday'}]"></ph-radio-group>
                    </ph-field>

                    <ph-field *ngIf="monthlyMode === 'day'">
                        <ph-label required>Day of the month</ph-label>
                        <ph-select formControlName="monthDay" ngDefaultControl [items]="monthDays"></ph-select>
                    </ph-field>

                    <ng-container *ngIf="monthlyMode === 'weekday'">
                        <ph-field>
                            <ph-label required>Which</ph-label>
                            <ph-select formControlName="ordinal" ngDefaultControl [items]="ordinals"></ph-select>
                        </ph-field>
                        <ph-field>
                            <ph-label required>Weekday</ph-label>
                            <ph-select formControlName="weekday" ngDefaultControl [items]="weekDays"></ph-select>
                        </ph-field>
                    </ng-container>
                </ph-stack>

                <ph-stack gap="tight" *ngIf="frequency === 'yearly'" data-test="yearly-box">
                    <ph-field>
                        <ph-label required>Month</ph-label>
                        <ph-select formControlName="month" ngDefaultControl [items]="months"></ph-select>
                    </ph-field>
                    <ph-field>
                        <ph-label required>Day</ph-label>
                        <ph-select formControlName="yearDay" ngDefaultControl [items]="yearDays"></ph-select>
                    </ph-field>
                </ph-stack>

                <ph-card flat>
                    <ph-stack gap="tight">
                        <strong data-test="cadence-preview">{{ cadenceText }}</strong>
                        <ph-hint *ngIf="firstRun" data-test="first-run">First run: {{ firstRun }}, in your organization's local time zone.</ph-hint>
                        <ph-hint *ngIf="!firstRun">Times are in your organization's local time zone.</ph-hint>
                    </ph-stack>
                </ph-card>
            </ph-stack>
        </ph-stack>

        <ph-stack *ngSwitchCase="'do'" data-test="step-do">

            <ng-container *ngIf="doMode === 'describe'">
                <form [formGroup]="doForm" novalidate>
                    <ph-field>
                        <ph-label required>Describe the work in your own words</ph-label>
                        <ph-textarea formControlName="intent"
                                     ngDefaultControl
                                     rows="4"
                                     data-autofocus
                                     [placeholder]="intentPlaceholder"></ph-textarea>
                    </ph-field>
                </form>

                <ph-link-button data-test="start-from-skill" (click)="useSkillMode()">Or, start from a skill</ph-link-button>
            </ng-container>

            <ng-container *ngIf="doMode === 'skill'">
                <ph-field [formGroup]="doForm">
                    <ph-label required>Skill</ph-label>
                    <ph-select formControlName="skillName"
                               ngDefaultControl
                               data-autofocus
                               [items]="skillItems"
                               [placeholder]="skillsLoading ? 'Loading skills' : 'Choose a skill'"></ph-select>
                    <ph-hint has-error *ngIf="skillsLoadFailed" role="alert">
                        Couldn't load the skills for this organization.
                        <ph-link-button (click)="loadSkills()">Try again</ph-link-button>
                    </ph-hint>
                    <ph-hint *ngIf="!skillsLoading && !skillsLoadFailed && skills.length === 0">
                        No skills are in reach for this organization yet.
                    </ph-hint>
                </ph-field>

                <ph-link-button data-test="describe-instead" (click)="useDescribeMode()">Describe it in my own words instead</ph-link-button>
            </ng-container>

            <ph-alert status="feature" variant="lighter" size="small">Gable will ask a question or two, then tell you what it will do before anything is saved.</ph-alert>
        </ph-stack>

        <ph-stack *ngSwitchCase="'then'" data-test="step-then">

            <ph-stack [formGroup]="thenForm">
                <ph-label required id="gable-workflow-outcomes-label">After each run</ph-label>
                <ph-stack gap="snug" role="group" aria-labelledby="gable-workflow-outcomes-label">
                    <ph-list-row variant="card" wrap
                                 heading="Stage suggestions in Activity for approval"
                                 meta="Anything Gable thinks should be done waits for you to approve it."
                                 (ph-select)="toggleOutcome('suggestions')">
                        <ph-checkbox slot="actions" formControlName="suggestions" ngDefaultControl data-autofocus aria-label="Stage suggestions in Activity for approval" data-test="outcome-suggestions"></ph-checkbox>
                    </ph-list-row>

                    <ph-list-row variant="card" wrap
                                 heading="Email a summary to people I choose"
                                 meta="Admins and board members of this organization."
                                 (ph-select)="toggleOutcome('email')">
                        <ph-checkbox slot="actions" formControlName="email" ngDefaultControl aria-label="Email a summary to people I choose" data-test="outcome-email"></ph-checkbox>
                        <ph-stack slot="detail" gap="tight" *ngIf="emailOn" data-test="recipients">
                            <app-people-picker *ngIf="recipientPeople !== null"
                                               [data]="recipientPeople"
                                               [preselectedPersonIds]="recipientIds"
                                               placeholder="Search admins and board members"
                                               (selectionsChange)="onRecipientsChange($event)"></app-people-picker>
                            <ph-hint *ngIf="recipientsLoading" role="status">Loading people</ph-hint>
                            <ph-hint has-error *ngIf="recipientsLoadFailed" role="alert">
                                Couldn't load the people for this organization.
                                <ph-link-button (click)="loadRecipients()">Try again</ph-link-button>
                            </ph-hint>
                            <ph-hint *ngIf="recipients.length > 0" data-test="recipients-count">{{ recipients.length }} selected</ph-hint>
                        </ph-stack>
                    </ph-list-row>

                    <ph-list-row variant="card" wrap
                                 heading="Save the summary as a document"
                                 meta="Each run's summary filed as a PDF with your documents."
                                 (ph-select)="toggleOutcome('document')">
                        <ph-checkbox slot="actions" formControlName="document" ngDefaultControl aria-label="Save the summary as a document" data-test="outcome-document"></ph-checkbox>
                        <ph-stack slot="detail" gap="tight" *ngIf="documentOn" data-test="document-folder">
                            <app-folder-picker [organizationId]="organizationId"
                                               [selectedFolderId]="documentFolderId"
                                               [showPathPreview]="false"
                                               (folderSelected)="onFolderSelected($event)"></app-folder-picker>
                            <ph-hint *ngIf="documentFolder" data-test="document-path">{{ documentPath }}</ph-hint>
                            <ph-hint *ngIf="!documentFolder" data-test="document-path-empty">Pick a folder in your documents.</ph-hint>
                        </ph-stack>
                    </ph-list-row>
                </ph-stack>
            </ph-stack>
        </ph-stack>

        <ph-stack *ngSwitchCase="'review'" data-test="step-review">
            <ph-stack *ngIf="reviewPhase === 'waiting'" gap="tight" data-test="waiting">
                <ph-chat-thinking [label]="waitingTitle" role="status" aria-live="polite" tabindex="-1" class="waiting-title" data-test="waiting-title"></ph-chat-thinking>
                <ph-hint data-test="waiting-hint">{{ waitingHint }}</ph-hint>
            </ph-stack>

            <div class="questions-step" *ngIf="reviewPhase === 'questions' && batch" data-test="questions-step">
                <app-question-card [batch]="batch"
                                   [bare]="true"
                                   [submitting]="intakeBusy"
                                   [backOnFirstPage]="true"
                                   [autoAdvance]="false"
                                   (submitAnswers)="onAnswers($event)"
                                   (close)="backToThen()"></app-question-card>
                <ph-stack *ngIf="intakeBusy" data-test="waiting">
                    <ph-chat-thinking class="waiting-title" [label]="waitingTitle" role="status" aria-live="polite" tabindex="-1" data-test="waiting-title"></ph-chat-thinking>
                </ph-stack>
            </div>

            <form *ngIf="reviewPhase === 'plan'" [formGroup]="reviewForm" (ngSubmit)="save()" novalidate><ph-stack>

                <ph-field>
                    <ph-label required>Title</ph-label>
                    <ph-input formControlName="title" ngDefaultControl placeholder="Board financial summary" data-autofocus></ph-input>
                </ph-field>

                <div>
                    <ph-list-row plain wrap data-test="review-when" heading="When">
                        <ph-link-button slot="actions" data-test="change-when" (click)="goToStep('when')">Change</ph-link-button>
                        <ph-stack slot="detail" gap="tight">
                            <div data-test="review-when-text">{{ cadenceText }}</div>
                            <ph-hint *ngIf="firstRun" data-test="review-when-note">{{ runLabel }}: {{ firstRun }}</ph-hint>
                        </ph-stack>
                    </ph-list-row>
                    <ph-list-row plain wrap data-test="review-do" heading="Do">
                        <ph-link-button slot="actions" data-test="change-do" (click)="goToStep('do')">Change</ph-link-button>
                        <ph-stack slot="detail" gap="tight">
                            <div data-test="review-do-text">{{ summaryText }}</div>
                            <ph-hint *ngIf="fromSkillName" data-test="review-do-note">From skill: {{ fromSkillName }}</ph-hint>
                        </ph-stack>
                    </ph-list-row>
                    <ph-list-row plain wrap data-test="review-then" heading="Then">
                        <ph-link-button slot="actions" data-test="change-then" (click)="goToStep('then')">Change</ph-link-button>
                        <ph-stack slot="detail" gap="tight">
                            <div data-test="review-then-text">{{ outcomesSummary }}</div>
                            <ph-alert *ngIf="thenNotice" status="warning" variant="lighter" size="small" role="status" data-test="then-notice">{{ thenNotice }}</ph-alert>
                        </ph-stack>
                    </ph-list-row>
                </div>
            </ph-stack></form>
        </ph-stack>
    </ph-card>

    <ph-actions align="between" *ngIf="showActions">
        <ph-button variant="secondary" mode="filled" type="button" *ngIf="step === 'when' || isEdit" [disabled]="isSaving" (click)="onCancel()">Cancel</ph-button>
        <ph-button variant="secondary" mode="filled" type="button" *ngIf="step !== 'when' && !isEdit" [disabled]="isSaving || intakeBusy" (click)="back()">Back</ph-button>
        <ph-button type="button" *ngIf="step !== 'review'" data-test="next" [disabled]="nextDisabled" (click)="next()">{{ primaryLabel }}</ph-button>
        <ph-button type="button" *ngIf="step === 'review'" data-test="save" [disabled]="saveDisabled" (click)="save()">{{ primaryLabel }}</ph-button>
    </ph-actions>
</ph-stack>
